import {BaseApi} from '@/common/api/BaseApi';
import {ApiVersionProperties} from '@/types/common/api/api.types';

/**
 * E00U6EKA5EGC — URLs of the 1:1 coaching. A client addresses their own room
 * with `me`, the coach with the client's id.
 */
export class IndividualCoachingApi {

  public static readonly OWN_CLIENT = 'me';

  public static root({version = 'v1'}: ApiVersionProperties = {}): string {
    return `${BaseApi.ROOT_URL}/api/${version}/individual-coaching`;
  }

  public static clients(options: ApiVersionProperties = {}): string {
    return `${IndividualCoachingApi.root(options)}/clients`;
  }

  public static client(clientId: string, options: ApiVersionProperties = {}): string {
    return `${IndividualCoachingApi.clients(options)}/${encodeURIComponent(clientId)}`;
  }

  /** T00PUU7W4Z5J — members a coach can start a coaching with. */
  public static candidates(options: ApiVersionProperties = {}): string {
    return `${IndividualCoachingApi.root(options)}/candidates`;
  }

  public static inbox(options: ApiVersionProperties = {}): string {
    return `${IndividualCoachingApi.root(options)}/inbox`;
  }

  public static calendar(from: string, to: string, options: ApiVersionProperties = {}): string {
    return `${IndividualCoachingApi.root(options)}/calendar?from=${from}&to=${to}`;
  }

  public static checkinTemplates(checkinId?: string, options: ApiVersionProperties = {}): string {
    const base = `${IndividualCoachingApi.root(options)}/checkins`;
    return checkinId ? `${base}/${encodeURIComponent(checkinId)}` : base;
  }

  // ---- client file (coach only) ----

  public static notes(clientId: string, noteId?: string): string {
    const base = `${IndividualCoachingApi.client(clientId)}/notes`;
    return noteId ? `${base}/${encodeURIComponent(noteId)}` : base;
  }

  public static noteFiles(clientId: string, noteId: string, fileId?: string): string {
    const base = `${IndividualCoachingApi.notes(clientId, noteId)}/files`;
    return fileId ? `${base}/${encodeURIComponent(fileId)}` : base;
  }

  // ---- plan ----

  public static plan(clientId: string): string {
    return `${IndividualCoachingApi.client(clientId)}/plan`;
  }

  public static goals(clientId: string, goalId?: string): string {
    const base = `${IndividualCoachingApi.client(clientId)}/goals`;
    return goalId ? `${base}/${encodeURIComponent(goalId)}` : base;
  }

  public static tasks(clientId: string, taskId?: string): string {
    const base = `${IndividualCoachingApi.client(clientId)}/tasks`;
    return taskId ? `${base}/${encodeURIComponent(taskId)}` : base;
  }

  public static taskDone(clientId: string, taskId: string): string {
    return `${IndividualCoachingApi.tasks(clientId, taskId)}/done`;
  }

  public static moduleSteps(clientId: string): string {
    return `${IndividualCoachingApi.client(clientId)}/module-steps`;
  }

  // ---- messages ----

  public static messages(clientId: string, filter: Record<string, string> = {}): string {
    const query = new URLSearchParams(filter).toString();
    return `${IndividualCoachingApi.client(clientId)}/messages${query ? `?${query}` : ''}`;
  }

  public static message(clientId: string, messageId: string): string {
    return `${IndividualCoachingApi.client(clientId)}/messages/${encodeURIComponent(messageId)}`;
  }

  /** T007HHZFPPNA — messages per session thread and day, for "Chat (n)" at each session. */
  public static sessionMessageCounts(clientId: string, from: string, to: string): string {
    return `${IndividualCoachingApi.client(clientId)}/messages/session-counts?from=${from}&to=${to}`;
  }

  public static messagesRead(clientId: string): string {
    return `${IndividualCoachingApi.client(clientId)}/messages/read`;
  }

  // ---- check-ins ----

  public static checkins(clientId: string, assignmentId?: string): string {
    const base = `${IndividualCoachingApi.client(clientId)}/checkins`;
    return assignmentId ? `${base}/${encodeURIComponent(assignmentId)}` : base;
  }

  public static checkinsDue(clientId: string, answerId?: string): string {
    const base = `${IndividualCoachingApi.client(clientId)}/checkins/due`;
    return answerId ? `${base}/${encodeURIComponent(answerId)}` : base;
  }

  public static checkinHistory(clientId: string, assignmentId: string): string {
    return `${IndividualCoachingApi.checkins(clientId, assignmentId)}/history`;
  }

  // ---- sessions & call ----

  public static sessions(clientId: string, sessionId?: string): string {
    const base = `${IndividualCoachingApi.client(clientId)}/sessions`;
    return sessionId ? `${base}/${encodeURIComponent(sessionId)}` : base;
  }

  public static sessionsInRange(clientId: string, from: string, to: string): string {
    return `${IndividualCoachingApi.sessions(clientId)}?from=${from}&to=${to}`;
  }

  public static nextSession(clientId: string): string {
    return `${IndividualCoachingApi.sessions(clientId)}/next`;
  }

  public static occurrence(clientId: string, sessionId: string, date: string): string {
    return `${IndividualCoachingApi.sessions(clientId, sessionId)}/occurrences/${date}`;
  }

  public static sessionAction(clientId: string, sessionId: string, action: 'confirm' | 'reschedule'): string {
    return `${IndividualCoachingApi.sessions(clientId, sessionId)}/${action}`;
  }

  public static call(clientId: string, sessionId: string, date: string, action: 'join' | 'signal' | 'leave'): string {
    return `${IndividualCoachingApi.occurrence(clientId, sessionId, date)}/${action}`;
  }

  // ---- recordings (T00EXMB3XI6N) ----

  public static recordings(clientId: string, recordingId?: string): string {
    const base = `${IndividualCoachingApi.client(clientId)}/recordings`;
    return recordingId ? `${base}/${encodeURIComponent(recordingId)}` : base;
  }

  /** The client consents to recording one call. */
  public static recordingConsent(clientId: string, sessionId: string, date: string): string {
    return `${IndividualCoachingApi.occurrence(clientId, sessionId, date)}/recordings`;
  }

  public static recordingAction(clientId: string, recordingId: string, action: 'chunks' | 'finish' | 'withdraw'): string {
    return `${IndividualCoachingApi.recordings(clientId, recordingId)}/${action}`;
  }

  /** Attachments are served by the file proxy, authenticated by the media cookie. */
  public static file(path: string): string {
    return `${BaseApi.IMAGE_URL}/files/${path}`;
  }
}
